// Shared atoms: HeroArt (photos over generative art), Stars, Constellation,
// Avatar, BottomTabs, IconGlyph, Toast, LocationMap. All warm/sunset tinted.

import React from 'react';
import { LOCATIONS } from './data.js';
import { milesFrom } from './lib/geo.js';

// ── LarkleMark ───────────────────────────────────────────────────────────────
// The brand mark: a small flock of birds crossing a warm sunset, rounded to a
// square. Reused wherever the wordmark appears (splash, welcome) — it's the
// same file as the PWA icon/favicon (public/icon.svg), so the mark is always
// pixel-identical everywhere it shows up.
export function LarkleMark({ size = 40, round = false }) {
  return <img src="/icon.svg" width={size} height={size} alt="" aria-hidden="true"
              style={{ display: 'block', borderRadius: round ? '50%' : undefined, objectFit: 'cover' }}/>;
}

// ── Switch ───────────────────────────────────────────────────────────────────
// A real on/off slider (track + sliding knob) — unlike a color-only toggle
// button, the knob position reads as on/off at a glance regardless of color
// perception, and it doubles as the label's click target.
export function Switch({ checked, onChange, label }) {
  return (
    <button
      role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        background: 'none', border: 0, padding: 0, cursor: 'pointer',
        fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
        color: checked ? 'var(--ink)' : 'var(--ink-mute)',
      }}
    >
      <span aria-hidden="true" style={{
        position: 'relative', width: 34, height: 20, borderRadius: 999, flexShrink: 0,
        background: checked ? 'var(--coral)' : 'rgba(42,26,26,0.18)',
        transition: 'background 0.2s ease',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: checked ? 16 : 2,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          boxShadow: '0 1px 3px rgba(42,26,26,0.35)',
          transition: 'left 0.2s cubic-bezier(.3,.7,.4,1)',
        }}/>
      </span>
      {label}
    </button>
  );
}

// ── HeroArt ────────────────────────────────────────────────────────────────
// Real place photos (place.images[].url) over a stylized "postcard" SVG
// placeholder. The SVG stays as the base layer so cards render instantly and
// photo failures degrade gracefully. Multi-photo navigation is a sliding
// filmstrip; photos are preloaded+decoded a window ahead so taps land on a
// ready image, and a slot still loading shows a sheen over the art.
// showNav=false renders just the first photo — no prev/next arrows or dots —
// crossfaded over the art. Use it for small thumbnails where controls don't fit.
// swipePhotos=true additionally lets a horizontal drag step through photos —
// only turned on where nothing else on the card needs that gesture (the
// discover deck's own card-swipe uses it for pass/save, so it stays off there).
export function HeroArt({ activity, height = 360, rounded = 28, showNav = true, swipePhotos = false }) {
  const photos = activity._place?.images || [];
  const [idx, setIdx] = React.useState(0);
  const [badUrls, setBadUrls] = React.useState(new Set());
  const [prevUrl, setPrevUrl] = React.useState(null);
  const [loadedUrls, setLoadedUrls] = React.useState(new Set());
  const [drag, setDrag] = React.useState(null); // { startX, dx } while a photo-swipe is in progress
  const preloadCache = React.useRef(new Map()); // url -> Image, also keeps preloads alive
  const imgRef = React.useRef(null);

  React.useEffect(() => { setIdx(0); setBadUrls(new Set()); setPrevUrl(null); }, [activity.id]);

  const goodPhotos = photos.filter(p => !badUrls.has(p.url));
  const photoUrl = goodPhotos[Math.min(idx, Math.max(0, goodPhotos.length - 1))]?.url ?? null;

  // prevUrl doubles as "last successfully loaded" — when it matches photoUrl the image is ready.
  const loaded = prevUrl === photoUrl && photoUrl !== null;

  const handleLoad = () => setPrevUrl(photoUrl);
  const handleError = () => { if (photoUrl) setBadUrls(b => new Set([...b, photoUrl])); };

  const markLoaded = (url) =>
    setLoadedUrls(prev => prev.has(url) ? prev : new Set(prev).add(url));

  // Warm a window around idx the moment the card renders or idx moves — not
  // after the current photo finishes loading, which quick taps always outran.
  // decode() so the photo can paint the instant it slides in, without a hitch.
  React.useEffect(() => {
    if (!(showNav || swipePhotos) || goodPhotos.length < 2) return;
    const lo = Math.max(0, idx - 1), hi = Math.min(goodPhotos.length - 1, idx + 2);
    for (let i = lo; i <= hi; i++) {
      const url = goodPhotos[i].url;
      if (preloadCache.current.has(url)) continue;
      const img = new window.Image();
      preloadCache.current.set(url, img);
      img.onload = () => {
        const done = () => markLoaded(url);
        img.decode ? img.decode().then(done, done) : done();
      };
      img.onerror = () => setBadUrls(b => new Set([...b, url]));
      img.src = url;
    }
  }, [idx, photos, badUrls]);

  // Release decoded bitmap memory when the card leaves the stack (swiped
  // away in Discover). preloadCache holds real Image objects with decode()
  // already forced on them — GC alone doesn't reliably reclaim that on iOS
  // Safari, and swiping through many photo-heavy cards without this crashes
  // the WebKit content process (blank white page). Android Chrome tolerates
  // the same pattern fine, which is why this only showed up on iPhone.
  React.useEffect(() => {
    return () => {
      preloadCache.current.forEach(img => {
        img.onload = null;
        img.onerror = null;
        img.src = '';
      });
      preloadCache.current.clear();
    };
  }, []);

  // A cached image can finish decoding before React attaches the onLoad
  // listener, so the event never fires and the card is stuck on the SVG
  // placeholder — most visible on the first card, whose photo is the most
  // likely to already be in the browser cache. Catch that case explicitly.
  React.useEffect(() => {
    if (imgRef.current?.complete) handleLoad();
  }, [photoUrl]);

  const canSwipe = swipePhotos && showNav && goodPhotos.length > 1;
  const onDragStart = (e) => {
    if (!canSwipe) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ startX: e.clientX, dx: 0 });
  };
  const onDragMove = (e) => { if (drag) setDrag(d => ({ ...d, dx: e.clientX - d.startX })); };
  const onDragEnd = () => {
    if (!drag) return;
    if (Math.abs(drag.dx) > 50) {
      if (drag.dx < 0) setIdx(i => Math.min(goodPhotos.length - 1, i + 1));
      else setIdx(i => Math.max(0, i - 1));
    }
    setDrag(null);
  };
  // Resist dragging past the first/last photo instead of just stopping dead.
  const rawDx = drag?.dx ?? 0;
  const dragDx = (rawDx > 0 && idx === 0) || (rawDx < 0 && idx === goodPhotos.length - 1) ? rawDx * 0.35 : rawDx;

  const h = activity.hue;
  const sky = `oklch(0.82 0.13 ${h})`;
  const horizon = `oklch(0.70 0.18 ${h})`;
  const ground = `oklch(0.42 0.14 ${h + 6})`;
  const sun = `oklch(0.88 0.16 ${(h + 18) % 360})`;
  const ink = `oklch(0.22 0.06 ${(h + 320) % 360})`;

  const W = 360, H = 480;
  const id = `g-${activity.id}`;

  // each art variant uses the same gradient base; only the foreground silhouette differs
  const variant = (() => {
    switch (activity.art) {
      case 'sunset-bridge': return (
        <g>
          <circle cx={200} cy={210} r={62} fill={sun} opacity={0.95} />
          {/* bridge silhouette */}
          <path d={`M0 360 L60 360 L70 230 L110 240 L120 360 L240 360 L250 240 L290 230 L300 360 L360 360 L360 ${H} L0 ${H} Z`} fill={ink} />
          <rect x={68} y={240} width={4} height={120} fill={ink} />
          <rect x={108} y={250} width={4} height={110} fill={ink} />
          <rect x={248} y={250} width={4} height={110} fill={ink} />
          <rect x={288} y={240} width={4} height={120} fill={ink} />
          <path d={`M0 358 Q180 348 360 358`} stroke={ink} strokeWidth={2} fill="none" />
        </g>
      );
      case 'lantern-tables': return (
        <g>
          <circle cx={280} cy={130} r={50} fill={sun} opacity={0.85} />
          {/* lanterns */}
          {[60, 120, 180, 240, 300].map((x, i) => (
            <g key={i}>
              <path d={`M${x} 60 L${x} 110`} stroke={ink} strokeWidth={1} opacity={0.6}/>
              <circle cx={x} cy={120 + (i%2)*8} r={11} fill={sun} stroke={ink} strokeWidth={0.8} opacity={0.95}/>
            </g>
          ))}
          {/* tables */}
          <rect x={20} y={320} width={140} height={14} rx={3} fill={ink} />
          <rect x={200} y={310} width={140} height={14} rx={3} fill={ink} />
          <rect x={30} y={334} width={4} height={50} fill={ink}/>
          <rect x={146} y={334} width={4} height={50} fill={ink}/>
          <rect x={210} y={324} width={4} height={56} fill={ink}/>
          <rect x={326} y={324} width={4} height={56} fill={ink}/>
        </g>
      );
      case 'sculpture-river': return (
        <g>
          <circle cx={120} cy={180} r={56} fill={sun} />
          {/* sculptures */}
          <rect x={50} y={240} width={32} height={110} fill={ink} />
          <circle cx={66} cy={232} r={20} fill={ink} />
          <path d={`M180 350 L230 220 L280 350 Z`} fill={ink} />
          <rect x={300} y={260} width={18} height={90} fill={ink} />
          {/* river */}
          <rect x={0} y={360} width={360} height={120} fill={ground} opacity={0.7}/>
          <path d={`M0 380 Q90 372 180 380 T360 380`} stroke={sun} strokeWidth={1.5} fill="none" opacity={0.7}/>
        </g>
      );
      case 'spotlight-mic': return (
        <g>
          <path d={`M120 80 L240 80 L340 480 L20 480 Z`} fill={sun} opacity={0.3}/>
          <circle cx={180} cy={300} r={22} fill={ink} />
          <rect x={176} y={322} width={8} height={80} fill={ink} />
          <rect x={140} y={400} width={80} height={6} fill={ink} />
          <path d={`M120 460 Q180 410 240 460`} stroke={ink} strokeWidth={2} fill="none" />
        </g>
      );
      case 'fish-lemon': return (
        <g>
          <circle cx={260} cy={130} r={48} fill={sun}/>
          {/* fish */}
          <ellipse cx={180} cy={290} rx={130} ry={50} fill={ink}/>
          <path d={`M310 290 L355 250 L355 330 Z`} fill={ink}/>
          <circle cx={130} cy={280} r={5} fill={sun}/>
          {/* lemons */}
          <circle cx={70} cy={400} r={20} fill={sun}/>
          <circle cx={110} cy={420} r={18} fill={sun}/>
          <circle cx={150} cy={400} r={20} fill={sun}/>
        </g>
      );
      case 'gantry-skyline': return (
        <g>
          <circle cx={180} cy={170} r={70} fill={sun} opacity={0.9}/>
          {/* skyline */}
          <rect x={0} y={280} width={26} height={80} fill={ink}/>
          <rect x={28} y={250} width={20} height={110} fill={ink}/>
          <rect x={50} y={290} width={24} height={70} fill={ink}/>
          <rect x={78} y={230} width={28} height={130} fill={ink}/>
          <rect x={110} y={260} width={20} height={100} fill={ink}/>
          <rect x={134} y={200} width={32} height={160} fill={ink}/>
          <rect x={170} y={250} width={22} height={110} fill={ink}/>
          <rect x={196} y={220} width={28} height={140} fill={ink}/>
          <rect x={228} y={270} width={22} height={90} fill={ink}/>
          <rect x={254} y={240} width={26} height={120} fill={ink}/>
          <rect x={284} y={260} width={22} height={100} fill={ink}/>
          <rect x={310} y={280} width={20} height={80} fill={ink}/>
          <rect x={334} y={260} width={26} height={100} fill={ink}/>
          {/* gantry */}
          <rect x={40} y={360} width={4} height={40} fill={ink}/>
          <rect x={80} y={360} width={4} height={40} fill={ink}/>
          <rect x={38} y={368} width={48} height={3} fill={ink}/>
        </g>
      );
      case 'mimosa-booth': return (
        <g>
          <rect x={40} y={120} width={280} height={260} rx={10} fill={ink} opacity={0.85}/>
          {/* glass */}
          <path d={`M150 200 L210 200 L195 320 L165 320 Z`} fill={sun}/>
          <rect x={175} y={320} width={10} height={40} fill={sun}/>
          <rect x={160} y={358} width={40} height={5} fill={sun}/>
        </g>
      );
      case 'pottery-wheel': return (
        <g>
          <circle cx={180} cy={150} r={60} fill={sun}/>
          <ellipse cx={180} cy={300} rx={140} ry={20} fill={ink}/>
          <ellipse cx={180} cy={280} rx={60} ry={10} fill={ink} opacity={0.6}/>
          <path d={`M150 280 L160 220 L200 220 L210 280 Z`} fill={ink}/>
          <rect x={170} y={310} width={20} height={80} fill={ink}/>
        </g>
      );
      case 'espresso-window': return (
        <g>
          <rect x={40} y={80} width={280} height={220} rx={6} fill={sun} opacity={0.5}/>
          <line x1={180} y1={80} x2={180} y2={300} stroke={ink} strokeWidth={2}/>
          <line x1={40} y1={190} x2={320} y2={190} stroke={ink} strokeWidth={2}/>
          {/* cup */}
          <rect x={140} y={350} width={80} height={50} rx={4} fill={ink}/>
          <ellipse cx={180} cy={350} rx={40} ry={6} fill={sun}/>
          <path d={`M220 360 Q240 370 240 380 Q240 390 220 392`} stroke={ink} strokeWidth={4} fill="none"/>
        </g>
      );
      case 'pub-glow': return (
        <g>
          <circle cx={180} cy={180} r={90} fill={sun} opacity={0.5}/>
          <rect x={70} y={300} width={50} height={90} rx={4} fill={ink}/>
          <rect x={150} y={290} width={50} height={100} rx={4} fill={ink}/>
          <rect x={230} y={295} width={50} height={95} rx={4} fill={ink}/>
          <ellipse cx={95} cy={300} rx={25} ry={6} fill={sun} opacity={0.6}/>
          <ellipse cx={175} cy={290} rx={25} ry={6} fill={sun} opacity={0.6}/>
          <ellipse cx={255} cy={295} rx={25} ry={6} fill={sun} opacity={0.6}/>
        </g>
      );
      case 'arcade-glow': return (
        <g>
          {[80, 180, 280].map((x, i) => (
            <g key={i}>
              <rect x={x-40} y={140} width={80} height={240} rx={6} fill={ink}/>
              <rect x={x-30} y={160} width={60} height={70} rx={4} fill={sun} opacity={0.8}/>
              <rect x={x-30} y={250} width={60} height={6} fill={sun} opacity={0.5}/>
            </g>
          ))}
        </g>
      );
      case 'copper-kettle': return (
        <g>
          <circle cx={180} cy={250} r={100} fill={sun}/>
          <rect x={140} y={130} width={80} height={30} rx={4} fill={ink}/>
          <path d={`M90 350 L270 350 L240 410 L120 410 Z`} fill={ink}/>
        </g>
      );
      case 'forest-path': return (
        <g>
          <path d={`M160 480 Q180 300 200 480 Z`} fill={ground} opacity={0.7}/>
          {[60, 120, 200, 280, 320].map((x, i) => (
            <g key={i}>
              <rect x={x-3} y={i%2 ? 200 : 240} width={6} height={i%2 ? 200 : 240} fill={ink}/>
              <circle cx={x} cy={i%2 ? 200 : 240} r={40 + (i%3)*8} fill={ground}/>
            </g>
          ))}
        </g>
      );
      case 'shelf-warm': return (
        <g>
          {[100, 180, 260, 340].map((y, i) => (
            <g key={i}>
              <rect x={40} y={y} width={280} height={4} fill={ink}/>
              {[60, 90, 120, 150, 180, 210, 240, 270, 300].map((x, j) => (
                <rect key={j} x={x} y={y-30-(j%3)*4} width={14} height={30+(j%3)*4} fill={`oklch(${0.4+(j%4)*0.08} 0.1 ${(h + j*30) % 360})`}/>
              ))}
            </g>
          ))}
        </g>
      );
      case 'smoke-meat': return (
        <g>
          <circle cx={260} cy={140} r={40} fill={sun} opacity={0.7}/>
          <path d={`M40 380 Q60 320 100 340 Q120 280 160 310 Q180 260 220 290 Q240 250 280 280 Q300 240 320 270 L340 380 Z`} fill={ink} opacity={0.5}/>
          <ellipse cx={180} cy={400} rx={120} ry={30} fill={ink}/>
          <ellipse cx={180} cy={395} rx={100} ry={20} fill={`oklch(0.4 0.15 ${h})`}/>
        </g>
      );
      default: return null;
    }
  })();

  return (
    <div
      onPointerDown={canSwipe ? onDragStart : undefined}
      onPointerMove={canSwipe ? onDragMove : undefined}
      onPointerUp={canSwipe ? onDragEnd : undefined}
      onPointerCancel={canSwipe ? onDragEnd : undefined}
      style={{
        position: 'relative',
        width: '100%',
        height,
        borderRadius: rounded,
        overflow: 'hidden',
        background: `linear-gradient(180deg, ${sky} 0%, ${horizon} 55%, ${ground} 100%)`,
        touchAction: canSwipe ? 'pan-y' : undefined,
        cursor: canSwipe ? (drag ? 'grabbing' : 'grab') : undefined,
      }}>
      {/* SVG art always rendered as the base — visible while any photo loads */}
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice"
           style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={sky}/>
            <stop offset="0.55" stopColor={horizon}/>
            <stop offset="1" stopColor={ground}/>
          </linearGradient>
        </defs>
        {variant}
      </svg>

      {(swipePhotos || showNav) && goodPhotos.length > 0 ? (
        // A real filmstrip: every photo sits side by side in one track, and
        // the whole track slides — the leaving photo moves out exactly as the
        // incoming one moves in, whether that's from a drag or a nav tap,
        // instead of the two crossfading in place. The slide fires on the tap
        // itself, so navigation always answers instantly even when the
        // incoming photo is still in flight — that slot shows the postcard
        // art with a sheen sweep until the photo fades in over it.
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', width: `${goodPhotos.length * 100}%`,
          transform: `translateX(calc(${-idx * (100 / goodPhotos.length)}% + ${dragDx}px))`,
          transition: drag ? 'none' : 'transform 0.3s cubic-bezier(.2,.8,.2,1)',
        }}>
          {goodPhotos.map((p, i) => {
            const ready = loadedUrls.has(p.url);
            return (
              <div key={p.url} style={{ width: `${100 / goodPhotos.length}%`, height: '100%', flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
                <img src={p.url} alt={i === idx ? activity.name : ''}
                     loading={Math.abs(i - idx) <= 1 ? 'eager' : 'lazy'}
                     // cached photos can complete before onLoad attaches — catch via ref
                     ref={el => { if (el?.complete && el.naturalWidth > 0) markLoaded(p.url); }}
                     style={{
                       position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                       opacity: ready ? 1 : 0, transition: 'opacity 0.35s ease',
                     }}
                     onLoad={() => markLoaded(p.url)}
                     onError={() => setBadUrls(b => new Set([...b, p.url]))}/>
                {!ready && Math.abs(i - idx) <= 1 && (
                  <div aria-hidden="true" style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.28) 50%, transparent 70%)',
                    backgroundSize: '200% 100%',
                    animation: 'hero-sheen 1.2s ease-in-out infinite',
                  }}/>
                )}
              </div>
            );
          })}
        </div>
      ) : (swipePhotos || showNav) ? null : (
        <>
          {/* Previously-loaded photo sits above SVG art — stays rendered through the crossfade so the SVG never shows through */}
          {prevUrl && (
            <img src={prevUrl} alt=""
                 style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}/>
          )}

          {/* Current photo fades in once loaded */}
          {photoUrl && (
            <img key={photoUrl} ref={imgRef} src={photoUrl} alt={activity.name}
                 style={{
                   position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                   opacity: loaded ? 1 : 0,
                   transition: 'opacity 0.35s ease',
                 }}
                 onLoad={handleLoad}
                 onError={handleError}/>
          )}
        </>
      )}

      {/* Photo nav — zIndex above SwipeCard's gradient/text overlays which are DOM siblings */}
      {showNav && goodPhotos.length > 1 && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none' }}>
          {idx > 0 && (
            <button
              aria-label="Previous photo"
              onPointerDown={e => { e.stopPropagation(); setIdx(i => Math.max(0, i - 1)); }}
              style={{
                pointerEvents: 'auto',
                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.45)', border: 0, borderRadius: '50%',
                width: 34, height: 34, color: '#fff', fontSize: 18, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(4px)',
              }}>‹</button>
          )}
          {idx < goodPhotos.length - 1 && (
            <button
              aria-label="Next photo"
              onPointerDown={e => { e.stopPropagation(); setIdx(i => Math.min(goodPhotos.length - 1, i + 1)); }}
              style={{
                pointerEvents: 'auto',
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.45)', border: 0, borderRadius: '50%',
                width: 34, height: 34, color: '#fff', fontSize: 18, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(4px)',
              }}>›</button>
          )}
          <div style={{
            position: 'absolute', bottom: 10, left: 0, right: 0,
            display: 'flex', justifyContent: 'center', gap: 5, pointerEvents: 'auto',
          }}>
            {goodPhotos.map((_, i) => (
              <div key={i}
                   onPointerDown={e => { e.stopPropagation(); setIdx(i); }}
                   style={{
                     width: i === idx ? 16 : 6, height: 6, borderRadius: 3,
                     background: i === idx ? '#fff' : 'rgba(255,255,255,0.5)',
                     transition: 'width 0.2s, background 0.2s', cursor: 'pointer',
                   }}/>
            ))}
          </div>
        </div>
      )}

      {/* subtle grain */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage:
          'radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), radial-gradient(rgba(0,0,0,0.08) 1px, transparent 1px)',
        backgroundSize: '3px 3px, 5px 5px',
        backgroundPosition: '0 0, 1px 2px',
        mixBlendMode: 'overlay',
        opacity: 0.5,
      }} />
    </div>
  );
}

// ── Stars ──────────────────────────────────────────────────────────────────
export function Stars({ value = 0, max = 5, onChange, size = 14 }) {
  const interactive = !!onChange;
  return (
    <div className="stars" style={{ gap: size > 20 ? 6 : 2 }}>
      {Array.from({ length: max }).map((_, i) => {
        const filled = i < value;
        return (
          <svg key={i} width={size} height={size} viewBox="0 0 24 24"
               role={interactive ? 'button' : undefined}
               aria-label={interactive ? `${i + 1} star${i ? 's' : ''}` : undefined}
               onClick={interactive ? () => onChange(i + 1) : undefined}
               style={{ cursor: interactive ? 'pointer' : 'default' }}>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"
                  fill={filled ? 'var(--gold)' : 'none'}
                  stroke={filled ? 'var(--gold)' : 'rgba(42,26,26,0.25)'}
                  strokeWidth={1.5} strokeLinejoin="round"/>
          </svg>
        );
      })}
    </div>
  );
}

// ── Avatar (initials in a colored circle) ──────────────────────────────────
export function Avatar({ name = '', hue = 18, initials, size = 32 }) {
  const i = initials || name.split(' ').map(s => s[0]).slice(0, 2).join('');
  return (
    <div className="avatar" style={{
      width: size, height: size, fontSize: size * 0.36,
      background: `oklch(0.62 0.16 ${hue})`,
    }}>{i}</div>
  );
}

// Friend avatar: real photo when the friend has one, generative art otherwise
// (also falls back if the photo URL 404s — expired Google profile photos, deleted uploads, etc.).
export function FriendAvatar({ friend, size = 42 }) {
  const rawUrl = friend?.photoUrl || friend?.profilePicture || null;
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [rawUrl]);

  if (rawUrl && !failed) {
    return <img src={rawUrl} alt={friend.name || ''}
                onError={() => setFailed(true)}
                style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}/>;
  }
  return <GenerativeAvatar userId={friend?.userId || 'unknown'} size={size}/>;
}

// ── GenerativeAvatar ───────────────────────────────────────────────────────
// Deterministic geometric SVG avatar seeded by userId. 6 pattern variants,
// warm oklch palette derived from the hash.
export function GenerativeAvatar({ userId = 'x', size = 42 }) {
  let h = 0;
  const s = userId || 'x';
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  const n = h >>> 0;

  const hue     = n % 360;
  const variant = (n >>> 6) % 6;
  const shift   = ((n >>> 12) % 80) - 40;

  const bg   = `oklch(0.60 0.18 ${hue})`;
  const lite = `oklch(0.84 0.10 ${(hue + 50) % 360})`;
  const deep = `oklch(0.32 0.12 ${(hue + 190) % 360})`;
  const mid  = `oklch(0.50 0.15 ${(hue + 120) % 360})`;

  const clipId = `gac-${n.toString(36)}`;
  const t = shift * 0.15;

  const art = (() => {
    switch (variant) {
      case 0: return (
        <>
          <circle cx={30+t} cy={30} r={22} fill="none" stroke={lite} strokeWidth={7} opacity={0.55}/>
          <circle cx={30+t} cy={30} r={12} fill="none" stroke={deep} strokeWidth={5} opacity={0.65}/>
          <circle cx={30+t} cy={30} r={4} fill={lite} opacity={0.95}/>
        </>
      );
      case 1: return (
        <>
          <circle cx={20+t} cy={23} r={17} fill={deep} opacity={0.45}/>
          <circle cx={40-t} cy={23} r={17} fill={lite} opacity={0.5}/>
          <circle cx={30} cy={40+t} r={15} fill={mid} opacity={0.55}/>
        </>
      );
      case 2: return (
        <>
          <polygon points={`30,${8+t} 52,30 30,${52-t} 8,30`} fill={deep} opacity={0.5}/>
          <polygon points={`30,${16+t} 44,30 30,${44-t} 16,30`} fill={lite} opacity={0.65}/>
          <circle cx={30} cy={30} r={7} fill={lite} opacity={0.95}/>
        </>
      );
      case 3: return (
        <>
          <polygon points={`30,${6+t} 55,50 5,50`} fill={deep} opacity={0.45}/>
          <polygon points={`30,${15+t} 48,46 12,46`} fill={mid} opacity={0.6}/>
          <polygon points={`30,${22+t} 42,43 18,43`} fill={lite} opacity={0.75}/>
        </>
      );
      case 4: return (
        <>
          {[0,60,120,180,240,300].map((deg, i) => (
            <ellipse key={i}
              cx={30 + Math.cos(deg * Math.PI / 180) * 9}
              cy={30 + Math.sin(deg * Math.PI / 180) * 9}
              rx={14} ry={6}
              transform={`rotate(${deg + t * 2} 30 30)`}
              fill={i % 2 === 0 ? deep : lite} opacity={0.48}/>
          ))}
          <circle cx={30} cy={30} r={7} fill={lite} opacity={0.9}/>
        </>
      );
      case 5: return (
        <>
          <path d={`M${12+t} 30 A 18 18 0 0 1 ${48+t} 30`} stroke={lite} strokeWidth={8} fill="none" opacity={0.55}/>
          <path d={`M${18-t} 36 A 12 12 0 0 0 ${42-t} 36`} stroke={deep} strokeWidth={6} fill="none" opacity={0.65}/>
          <circle cx={30} cy={15+t*0.3} r={7} fill={deep} opacity={0.7}/>
          <circle cx={17+t*0.3} cy={43} r={5} fill={lite} opacity={0.7}/>
          <circle cx={43-t*0.3} cy={43} r={5} fill={lite} opacity={0.7}/>
        </>
      );
      default: return null;
    }
  })();

  return (
    <svg width={size} height={size} viewBox="0 0 60 60" style={{ flexShrink: 0 }}>
      <defs>
        <clipPath id={clipId}><circle cx={30} cy={30} r={30}/></clipPath>
      </defs>
      <circle cx={30} cy={30} r={30} fill={bg}/>
      <g clipPath={`url(#${clipId})`}>{art}</g>
    </svg>
  );
}

// ── Constellation ──────────────────────────────────────────────────────────
// 9-axis radar: user vibe (dashed) vs place vibe (coral fill).
const RADAR_AXES = [
  { key: 'physical',  label: 'Active'  },
  { key: 'social',    label: 'Social'  },
  { key: 'outdoor',   label: 'Outdoor' },
  { key: 'newPeople', label: 'Open'    },
  { key: 'creative',  label: 'Make'    },
  { key: 'boozy',     label: 'Boozy'   },
  { key: 'eating',    label: 'Food'    },
  { key: 'expensive', label: 'Splurge' },
];

export function Constellation({ user, activity, size = 100, light = false }) {
  const N  = RADAR_AXES.length;
  const CX = 100, CY = 100;
  const R  = 48;   // data polygon radius
  const LR = 72;   // label radius (generous gap so text never clips)

  const angle = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / N;
  const pt = (vibe, i) => {
    const v = (vibe[RADAR_AXES[i].key] ?? 50) / 100;
    return [CX + Math.cos(angle(i)) * R * v, CY + Math.sin(angle(i)) * R * v];
  };
  const poly = (vibe) => RADAR_AXES.map((_, i) => pt(vibe, i).join(',')).join(' ');
  const ring = (k) => RADAR_AXES.map((_, i) =>
    [CX + Math.cos(angle(i)) * R * k, CY + Math.sin(angle(i)) * R * k].join(',')
  ).join(' ');

  const labelColor = light ? 'rgba(255,255,255,0.88)' : 'rgba(42,26,26,0.58)';
  const gridColor  = light ? 'rgba(255,255,255,0.18)' : 'rgba(42,26,26,0.11)';
  const userColor  = light ? 'rgba(255,255,255,0.65)' : 'rgba(42,26,26,0.35)';
  const showLabels = size >= 80;

  return (
    <svg width={size} height={size} viewBox="0 0 200 200" overflow="visible">
      <polygon points={ring(1)}
               fill={light ? 'rgba(255,255,255,0.04)' : 'rgba(42,26,26,0.025)'}/>

      {[0.33, 0.66, 1].map(k => (
        <polygon key={k} points={ring(k)} fill="none"
                 stroke={gridColor} strokeWidth={0.8}/>
      ))}

      {RADAR_AXES.map((_, i) => {
        const [x, y] = [CX + Math.cos(angle(i)) * R, CY + Math.sin(angle(i)) * R];
        return <line key={i} x1={CX} y1={CY} x2={x} y2={y}
                     stroke={gridColor} strokeWidth={0.6}/>;
      })}

      <polygon points={poly(user)}
               fill={userColor} fillOpacity={0.12}
               stroke={userColor} strokeWidth={1.5} strokeDasharray="3 2"/>

      <polygon points={poly(activity.vibe)}
               fill="var(--coral)" fillOpacity={0.42}
               stroke="var(--coral)" strokeWidth={2}/>

      {RADAR_AXES.map((a, i) => {
        const v = (activity.vibe[a.key] ?? 50) / 100;
        if (v < 0.08) return null;
        const [x, y] = pt(activity.vibe, i);
        return <circle key={i} cx={x} cy={y} r={3} fill="var(--coral)" opacity={0.9}/>;
      })}

      {showLabels && RADAR_AXES.map((a, i) => {
        const [x, y] = [CX + Math.cos(angle(i)) * LR, CY + Math.sin(angle(i)) * LR];
        return (
          <text key={a.key} x={x} y={y}
                fontSize={9.5} fontWeight={650}
                fill={labelColor} textAnchor="middle" dominantBaseline="middle"
                letterSpacing="0.04em" style={{ textTransform: 'uppercase' }}>
            {a.label}
          </text>
        );
      })}
    </svg>
  );
}

// ── Tiny vibe ribbon for cards in lists ────────────────────────────────────
export function VibeRibbon({ vibe }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {['physical', 'social', 'outdoor'].map(k => {
        const v = vibe[k];
        return (
          <div key={k} style={{
            width: 22, height: 4, borderRadius: 999,
            background: `linear-gradient(to right, var(--coral) ${v}%, rgba(42,26,26,0.12) ${v}%)`,
          }}/>
        );
      })}
    </div>
  );
}

// ── LocationMap ────────────────────────────────────────────────────────────
// Leaflet is loaded lazily so the map library stays out of the main bundle.
export function LocationMap({ base, locationId, distance, setDistance, activities, onChangeLocation, customGPS, onMapClick, onClearCustom }) {
  const containerRef  = React.useRef(null);
  const mapRef        = React.useRef(null);
  const layersRef     = React.useRef([]);
  const onMapClickRef = React.useRef(onMapClick);
  const [L, setL] = React.useState(null);
  const [mapReady, setMapReady] = React.useState(false);
  React.useEffect(() => { onMapClickRef.current = onMapClick; }, [onMapClick]);

  React.useEffect(() => {
    let alive = true;
    Promise.all([import('leaflet'), import('leaflet/dist/leaflet.css')])
      .then(([mod]) => { if (alive) setL(mod.default ?? mod); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const here  = LOCATIONS.find(l => l.id === locationId);
  const coral = '#E5613A';

  const predefinedGPS = () => LOCATIONS.find(l => l.id === locationId) || LOCATIONS[0];
  const centerGPS     = () => customGPS || predefinedGPS();

  // Init map once Leaflet is loaded
  React.useEffect(() => {
    if (!L || !containerRef.current || mapRef.current) return;
    const { lat, lon } = centerGPS();
    mapRef.current = L.map(containerRef.current, {
      zoomControl: false,
      attributionControl: false,
    }).setView([lat, lon], 12);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(mapRef.current);

    L.control.attribution({ prefix: false, position: 'bottomleft' })
      .addAttribution('<span style="font-size:9px;opacity:0.5">© OpenStreetMap</span>')
      .addTo(mapRef.current);

    mapRef.current.on('click', (e) => {
      onMapClickRef.current?.(e.latlng.lat, e.latlng.lng);
    });
    setMapReady(true);

    return () => { mapRef.current?.remove(); mapRef.current = null; setMapReady(false); };
  }, [L]);

  // Pan to predefined neighbourhood — only when locationId changes and no
  // custom pin. Keeps the user's current zoom.
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || customGPS) return;
    const { lat, lon } = predefinedGPS();
    map.setView([lat, lon], map.getZoom(), { animate: true, duration: 0.4 });
  }, [locationId, mapReady]);

  // Pan to dropped pin when customGPS is set. Keeps the user's current zoom.
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !customGPS) return;
    map.setView([customGPS.lat, customGPS.lon], map.getZoom(), { animate: true, duration: 0.3 });
  }, [customGPS, mapReady]);

  // Re-draw overlays whenever location, distance, activities, or custom pin change
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !L) return;

    layersRef.current.forEach(l => l.remove());
    layersRef.current = [];

    const { lat, lon } = centerGPS();

    // radius circle
    const circle = L.circle([lat, lon], {
      radius: distance * 1609,
      color: coral, fillColor: coral,
      fillOpacity: 0.07, weight: 2, dashArray: '6 4',
    }).addTo(map);
    layersRef.current.push(circle);

    // activity dots
    activities.forEach(a => {
      if (a.lat == null) return;
      const within = milesFrom(a, base) <= distance;
      const dot = L.circleMarker([a.lat, a.lon], {
        radius: within ? 7 : 4,
        fillColor: within ? `oklch(0.62 0.16 ${a.hue})` : 'rgba(42,26,26,0.22)',
        color: within ? 'white' : 'transparent',
        weight: 1.5,
        fillOpacity: 0.9,
      }).addTo(map);
      if (within) dot.bindTooltip(a.name, { direction: 'top', className: 'larkle-tip' });
      layersRef.current.push(dot);
    });

    // home-base pin
    const pin = L.divIcon({
      html: `<svg width="26" height="32" viewBox="0 0 26 32" style="filter:drop-shadow(0 2px 4px rgba(0,0,0,.3))">
               <path d="M13 0C6 0 .5 5.4.5 12c0 8.5 12.5 20 12.5 20S25.5 20.5 25.5 12C25.5 5.4 20 0 13 0z" fill="${coral}"/>
               <circle cx="13" cy="12" r="5" fill="white"/>
             </svg>`,
      iconSize: [26, 32], iconAnchor: [13, 32], className: '',
    });
    layersRef.current.push(L.marker([lat, lon], { icon: pin }).addTo(map));

  }, [locationId, distance, activities.length, customGPS, mapReady]);

  const chipStyle = {
    position: 'absolute', zIndex: 1000,
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
    border: 0, borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 1px 4px rgba(0,0,0,0.14)',
  };

  return (
    <div style={{
      borderRadius: 22, overflow: 'hidden',
      border: '0.5px solid var(--hair)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      marginBottom: 18,
    }}>
      <div style={{ position: 'relative', width: '100%', height: 220, zIndex: 0 }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%', cursor: 'crosshair', background: 'var(--bg-tint)' }}/>

        {/* location chip — neighbourhood picker or dropped-pin label */}
        {customGPS ? (
          <div style={{ ...chipStyle, top: 10, left: 10, padding: '5px 8px 5px 11px', cursor: 'default' }}>
            <span style={{ width: 13, height: 13, color: coral }}>{Icon.pin}</span>
            <span className="serif" style={{ fontSize: 15, color: 'var(--ink)', lineHeight: 1 }}>Dropped pin</span>
            <button onClick={onClearCustom} aria-label="Clear pin" style={{
              border: 0, background: 'rgba(42,26,26,0.08)', borderRadius: '50%',
              width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', padding: 0, marginLeft: 2,
            }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                <path d="M6 6l12 12M18 6L6 18" stroke="var(--ink)" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        ) : (
          <button onClick={onChangeLocation} style={{ ...chipStyle, top: 10, left: 10, padding: '6px 10px 6px 11px' }}>
            <span style={{ width: 13, height: 13, color: coral }}>{Icon.pin}</span>
            <span className="serif" style={{ fontSize: 15, color: 'var(--ink)', lineHeight: 1 }}>{here?.name}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path d="M7 10l5 5 5-5" stroke="var(--ink-mute)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {/* tap-to-pin hint */}
        {!customGPS && (
          <div style={{
            position: 'absolute', bottom: 10, left: 10, zIndex: 1000,
            background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(6px)',
            borderRadius: 8, padding: '3px 8px', fontSize: 10.5,
            color: 'var(--ink-mute)', pointerEvents: 'none',
          }}>
            Tap map to drop a pin
          </div>
        )}
      </div>

      {/* radius slider */}
      <div style={{ padding: '12px 14px 14px', background: 'var(--paper)' }}>
        <div className="row between" style={{ gap: 12 }}>
          <span className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Search radius
          </span>
          <input type="range" min={0.5} max={6} step={0.5} value={distance}
                 aria-label="Search radius in miles"
                 onChange={e => setDistance(Number(e.target.value))}
                 style={{ flex: 1, accentColor: coral, '--range-frac': (distance - 0.5) / (6 - 0.5) }}/>
          <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', minWidth: 38, textAlign: 'right' }} className="tabnum">
            {distance} mi
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Price dots ─────────────────────────────────────────────────────────────
export function PriceDots({ n }) {
  if (n === 0) return <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.04, color: 'var(--green)' }}>FREE</span>;
  return (
    <span style={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.5, color: 'var(--ink)' }}>
      {'$'.repeat(n)}<span style={{ color: 'var(--ink-mute)' }}>{'$'.repeat(4 - n)}</span>
    </span>
  );
}

// ── Toast ──────────────────────────────────────────────────────────────────
export function Toast({ msg, onDone }) {
  React.useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onDone, 1900);
    return () => clearTimeout(t);
  }, [msg]);
  if (!msg) return null;
  return <div className="toast" role="status">{msg}</div>;
}

// ── Icon glyphs (line) ─────────────────────────────────────────────────────
export const Icon = {
  flame: <svg viewBox="0 0 24 24" fill="none"><path d="M12 3c1 4 5 5 5 10a5 5 0 1 1-10 0c0-2 1-3 2-4-1 2 0 4 2 4 0-3-2-5 1-10z" stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round"/></svg>,
  heart: <svg viewBox="0 0 24 24" fill="none"><path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round"/></svg>,
  heartFill: <svg viewBox="0 0 24 24"><path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" fill="currentColor"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none"><path d="M5 12l4.5 4.5L19 7" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/></svg>,
  checkCircle: <svg viewBox="0 0 24 24" fill="none"><circle cx={12} cy={12} r={9.5} stroke="currentColor" strokeWidth={1.6}/><path d="M8 12.5l3 3 5-6" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth={2} strokeLinecap="round"/></svg>,
  friends: <svg viewBox="0 0 24 24" fill="none"><circle cx={9} cy={9} r={3.5} stroke="currentColor" strokeWidth={1.6}/><path d="M2.5 19c.5-3 3-5 6.5-5s6 2 6.5 5" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"/><circle cx={17} cy={8} r={2.5} stroke="currentColor" strokeWidth={1.6}/><path d="M16 14c2.5.5 4.5 2 5 5" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"/></svg>,
  compass: <svg viewBox="0 0 24 24" fill="none"><circle cx={12} cy={12} r={9.5} stroke="currentColor" strokeWidth={1.6}/><path d="M15.5 8.5L13 13l-4.5 2.5L11 11l4.5-2.5z" fill="currentColor"/></svg>,
  passport: <svg viewBox="0 0 24 24" fill="none"><rect x={5} y={3} width={14} height={18} rx={2} stroke="currentColor" strokeWidth={1.6}/><circle cx={12} cy={10} r={3} stroke="currentColor" strokeWidth={1.6}/><path d="M8 16h8" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"/></svg>,
  share: <svg viewBox="0 0 24 24" fill="none"><circle cx={6} cy={12} r={2.5} stroke="currentColor" strokeWidth={1.6}/><circle cx={18} cy={6} r={2.5} stroke="currentColor" strokeWidth={1.6}/><circle cx={18} cy={18} r={2.5} stroke="currentColor" strokeWidth={1.6}/><path d="M8 11l8-4M8 13l8 4" stroke="currentColor" strokeWidth={1.6}/></svg>,
  info: <svg viewBox="0 0 24 24" fill="none"><circle cx={12} cy={12} r={9.5} stroke="currentColor" strokeWidth={1.6}/><path d="M12 11v6" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"/><circle cx={12} cy={8} r={1} fill="currentColor"/></svg>,
  bookmark: <svg viewBox="0 0 24 24" fill="none"><path d="M6 4h12v17l-6-4-6 4V4z" stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round"/></svg>,
  bookmarkFill: <svg viewBox="0 0 24 24"><path d="M6 4h12v17l-6-4-6 4V4z" fill="currentColor"/></svg>,
  pin: <svg viewBox="0 0 24 24" fill="none"><path d="M12 22s7-7 7-12a7 7 0 1 0-14 0c0 5 7 12 7 12z" stroke="currentColor" strokeWidth={1.6}/><circle cx={12} cy={10} r={2.5} stroke="currentColor" strokeWidth={1.6}/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none"><circle cx={12} cy={12} r={9.5} stroke="currentColor" strokeWidth={1.6}/><path d="M12 7v5l3 2" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"/></svg>,
  link: <svg viewBox="0 0 24 24" fill="none"><path d="M10 14a4 4 0 0 1 0-5l3-3a4 4 0 0 1 6 6l-2 2M14 10a4 4 0 0 1 0 5l-3 3a4 4 0 0 1-6-6l2-2" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"/></svg>,
  msg: <svg viewBox="0 0 24 24" fill="none"><path d="M4 6h16v11H8l-4 3V6z" stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round"/></svg>,
  cal: <svg viewBox="0 0 24 24" fill="none"><rect x={4} y={5} width={16} height={15} rx={2} stroke="currentColor" strokeWidth={1.6}/><path d="M4 9h16M9 3v4M15 3v4" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"/></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none"><path d="M4 13h4l2 3h4l2-3h4" stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round"/><path d="M4 13V6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6z" stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round"/></svg>,
};

// ── Bottom tabs ────────────────────────────────────────────────────────────
export function BottomTabs({ current, onChange, badges = {} }) {
  const tabs = [
    { id: 'vibe',     label: 'Vibe',    icon: Icon.compass },
    { id: 'discover', label: 'Discover', icon: Icon.flame },
    { id: 'saved',    label: 'Saved',   icon: Icon.bookmark },
    { id: 'friends',  label: 'Friends', icon: Icon.friends },
    { id: 'profile',  label: 'Profile', icon: <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth={1.6}/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"/></svg> },
  ];
  return (
    <div className="tabbar">
      {tabs.map(t => (
        <button key={t.id} className={`tab ${current === t.id ? 'active' : ''}`}
                aria-label={t.label}
                onClick={() => onChange(t.id)}>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            {t.icon}
            {badges[t.id] > 0 && (
              <span className="tab-badge">{badges[t.id] > 9 ? '9+' : badges[t.id]}</span>
            )}
          </span>
          <span>{t.label}</span>
        </button>
      ))}
    </div>
  );
}
